/**
 * Helpers for picking monochrome variants that contrast with the background
 * they sit on top of. Used by the header and footer (which paint `bg-primary`)
 * to flip the property logo, the Multihub wordmark, and the Repli mark
 * between white and black so they read clearly on any brand color.
 *
 * Why we don't re-implement WCAG luminance here:
 * `@vibesites/tokens` already runs the WCAG luminance check on the primary
 * (and secondary) brand colors and exposes the resulting contrast text color
 * via `useTokens().color.textOnPrimary` / `textOnSecondary`. Per
 * `vendor/vibesites/tokens/generator.ts#getContrastTextColor`, that value is
 * always `#ffffff` (over a dark background) or `#1a1a1a` (over a light one).
 *
 * Reading that already-computed token instead of recomputing luminance keeps
 * the contrast decision in one place and means the theme's logo flip stays in
 * sync with whatever threshold the tokens package decides on.
 */
const LIGHT_TEXT_VALUES = new Set(['#ffffff', '#fff', 'white']);

/**
 * Returns true when the supplied `textOnBackground` color indicates the
 * design system has selected a light/white foreground — i.e., the underlying
 * background is dark and we should use the white logo variant.
 *
 * Defaults to `true` when the value is missing so the white logo (the more
 * common case for branded headers/footers) is preferred during early renders
 * before tokens have been computed.
 */
export function isDarkBackground(textOnBg: string | null | undefined): boolean {
  if (!textOnBg) return true;
  return LIGHT_TEXT_VALUES.has(textOnBg.trim().toLowerCase());
}

/**
 * Pick the mono logo variant whose color matches the supplied
 * `textOnBackground` token. Falls back through the chain when the preferred
 * variant is missing so the page still renders something useful.
 *
 * @param textOnBg The contrast text color emitted by the design tokens (e.g.
 *   `tokens.color.textOnPrimary`). When this is the "white" sentinel the
 *   background is dark, so we prefer the mono-white logo, and vice versa.
 */
export function pickContrastLogo(
  textOnBg: string | null | undefined,
  options: {
    monoWhite?: string | null;
    monoBlack?: string | null;
    fallback?: string | null;
  },
): string | null {
  const { monoWhite, monoBlack, fallback } = options;
  if (isDarkBackground(textOnBg)) {
    return monoWhite || monoBlack || fallback || null;
  }
  return monoBlack || monoWhite || fallback || null;
}
